import api from './api';
import type { ApiResponse } from '@/types/api.types';

/**
 * watcher_offline scope (QA #100): 'all' = every watcher the config's
 * creator can see, including ones added later; 'selected' = watcher_ids only.
 */
export type WatcherScope = 'all' | 'selected';

export interface AlertWatcherRef {
  id: string;
  name: string;
}

export interface AlertConfig {
  id: string;
  user_id: string;
  alert_email: string;
  events: string[];
  is_enabled: boolean;
  dedup_window_minutes: number;
  watcher_scope: WatcherScope;
  watcher_ids: string[];
  watchers: AlertWatcherRef[];
  created_at: string;
  updated_at: string;
}

export interface CreateAlertConfigRequest {
  alert_email: string;
  // Backend CreateAlertConfigRequest binds `events` as required,min=1
  events: string[];
  dedup_window_minutes?: number;
  watcher_scope?: WatcherScope;
  watcher_ids?: string[];
}

export interface UpdateAlertConfigRequest {
  alert_email?: string;
  events?: string[];
  is_enabled?: boolean;
  dedup_window_minutes?: number;
  watcher_scope?: WatcherScope;
  watcher_ids?: string[];
}

export const alertService = {
  // User alert config CRUD
  async listConfigs(): Promise<AlertConfig[]> {
    const response = await api.get<ApiResponse<AlertConfig[]>>('/api/v1/alerts/configs');
    return response.data.data || [];
  },

  async createConfig(data: CreateAlertConfigRequest): Promise<AlertConfig> {
    const response = await api.post<ApiResponse<AlertConfig>>('/api/v1/alerts/configs', data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to create alert config');
  },

  async updateConfig(id: string, data: UpdateAlertConfigRequest): Promise<AlertConfig> {
    const response = await api.put<ApiResponse<AlertConfig>>(`/api/v1/alerts/configs/${id}`, data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to update alert config');
  },

  async deleteConfig(id: string): Promise<void> {
    const response = await api.delete<ApiResponse>(`/api/v1/alerts/configs/${id}`);
    if (!response.data.success) throw new Error('Failed to delete alert config');
  },

  async sendTestEmail(email: string): Promise<void> {
    const response = await api.post<ApiResponse>('/api/v1/alerts/test', { email });
    if (!response.data.success) throw new Error('Failed to send test email');
  },
};
